'use client'

import {
  referencesProductArticleDetailShownAtom,
  referencesProductSelectedColumnsAtom,
} from '@/feature/product/services/referencesProduct.store'
import { useReferencesProductRow } from '@/feature/product/hooks/useReferencesProductRow'
import type { MergedArticle } from '@/feature/product/types/mergedProduct'
import type { ProductDisplayPrice } from '@/feature/product/utils/productDisplayPrice'
import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { Divider } from '@/ui/layouts/divider/Divider'
import { TextSkeleton } from '@/ui/skeleton/TextSkeleton'
import {
  type ProductAvailabilities_PickupPointOfSaleFragment,
  type ProductAvailabilities_ProductFragment,
  type ProductBuyBox_CartFragment,
  type ReferencesProductTable_ArticleFragment,
  type ReferencesProductTableRows_PriceAndStockFragment,
} from '@gql-generated/graphql'
import { useAtomValue } from 'jotai'
import React, { Suspense, useMemo } from 'react'
import { EquivalentProductsLink } from '../../../equivalent-products/EquivalentProductsLink.client'
import { ProductAvailabilities } from '../../../product-availabilities/ProductAvailabilities.client'
import { ProductAvailabilitiesSkeleton } from '../../../product-availabilities/ProductAvailabilitiesSkeleton'
import { ProductAvailabilityIssueCard } from '../../../product-availability-issue-card/ProductAvailabilityIssueCard.client'
import { ProductPrice } from '../../../product-price/ProductPrice.client'
import { ProductTag } from '../../../product-tag/ProductTag.client'
import { ProductTiersPricesInfoCard } from '../../../product-tiers-prices/ProductTiersPricesInfoCard.client'
import { ButtonDetailsShown } from './ButtonDetailsShown.client'
import { useReferencesProductTableContext } from './ReferencesProductContext'
import { ReferencesProductTableAddToCart } from './ReferencesProductTableAddToCart.client'
import { ReferencesProductTableAddToCartSkeleton } from './ReferencesProductTableAddToCartSkeleton'
import { ReferencesProductTechnicalSpecs } from './ReferencesProductTechnicalSpecs.client'

const AvailabilityCell = React.memo(function AvailabilityCell({
  isArticlesPriceAndStockLoading,
  isAvailabilityDisplayable,
  pickupPointOfSale,
  article,
  isClickAndCollectEnabled,
}: {
  isArticlesPriceAndStockLoading: boolean
  isAvailabilityDisplayable: boolean
  pickupPointOfSale?: ProductAvailabilities_PickupPointOfSaleFragment | null
  article: MergedArticle
  isClickAndCollectEnabled?: boolean
  isProductPriceDisplayable: boolean
}) {
  if (isArticlesPriceAndStockLoading) {
    return <ProductAvailabilitiesSkeleton offset={[{ nbOfLine: 1, title: false }]} />
  }

  if (!isAvailabilityDisplayable) {
    return null
  }

  return (
    <ProductAvailabilities
      pickupPointOfSale={pickupPointOfSale}
      product={article as ProductAvailabilities_ProductFragment}
      isTitleVisible={false}
      isClickAndCollectEnabled={isClickAndCollectEnabled}
      shortMode
      notificationClassName="mr-1 shrink-0"
    />
  )
})

const PriceCell = React.memo(function PriceCell({
  isArticlesPriceAndStockLoading,
  isAvailableForSale,
  isAuthenticated,
  hasPrice,
  isPublicPriceEnabled,
  article,
  displayPrice,
  selectedQuantity,
  cart,
  isProductPriceDisplayable,
}: {
  isArticlesPriceAndStockLoading: boolean
  isAvailableForSale: boolean
  isAuthenticated: boolean
  hasPrice: boolean
  isPublicPriceEnabled: boolean
  article: MergedArticle
  displayPrice: ProductDisplayPrice
  selectedQuantity?: number
  cart?: ProductBuyBox_CartFragment
  isProductPriceDisplayable: boolean
}) {
  // Disconnected user on non-public-price site: skip price guards so AddToCart can show the login link.
  const showLoginLink = !isAuthenticated && !isPublicPriceEnabled

  if (isArticlesPriceAndStockLoading) {
    return <TextSkeleton className="w-18" size="xl" leading="normal" />
  }

  if (!isProductPriceDisplayable && !showLoginLink) {
    return <ProductAvailabilityIssueCard issue="noPricesAvailable" />
  }

  if (!hasPrice && !showLoginLink) {
    return isPublicPriceEnabled ? <ProductAvailabilityIssueCard issue="noPricesAvailable" /> : null
  }

  return (
    <>
      <ProductPrice
        size="xl"
        price={displayPrice.price}
        priceQuantity={displayPrice.priceQuantity}
        unitPrice={displayPrice.erpPrice}
        discount={{
          price: displayPrice.discountAmount,
          percentage: displayPrice.discountPercentage,
          tiersPrices: displayPrice.tiersPrices,
        }}
        selectedQuantity={selectedQuantity}
        packaging={article.stock?.packaging}
        className="desktop:justify-items-end forbidden-desktop-ref-table:justify-items-start"
      />
      {isAvailableForSale || showLoginLink ? (
        <div className="mt-1 forbidden-desktop-ref-table:hidden">
          <ReferencesProductTableAddToCart
            cart={cart}
            article={
              article as ReferencesProductTable_ArticleFragment &
                ReferencesProductTableRows_PriceAndStockFragment
            }
          />
        </div>
      ) : (
        article.isEquivalentProductsLinkVisible &&
        article.urlKey && (
          <div className="mt-1 forbidden-desktop-ref-table:hidden">
            <EquivalentProductsLink slug={article.urlKey} className="text-sm" />
          </div>
        )
      )}
    </>
  )
})

const ActionCell = React.memo(function ActionCell({
  isAvailableForSale,
  isAuthenticated,
  isArticlesPriceAndStockLoading,
  hasPrice,
  cart,
  article,
  setSelectedQuantity,
  isMonoDiscriminant,
}: {
  isAvailableForSale: boolean
  isAuthenticated: boolean
  isArticlesPriceAndStockLoading: boolean
  hasPrice: boolean
  cart?: ProductBuyBox_CartFragment
  article: MergedArticle
  setSelectedQuantity: (quantity: number) => void
  isMonoDiscriminant: boolean
}) {
  const addToCart = (
    <Suspense fallback={<ReferencesProductTableAddToCartSkeleton />}>
      <ReferencesProductTableAddToCart
        cart={cart}
        article={
          article as ReferencesProductTable_ArticleFragment &
            ReferencesProductTableRows_PriceAndStockFragment
        }
        onQuantityChange={setSelectedQuantity}
        isMonoDiscriminant={isMonoDiscriminant}
      />
    </Suspense>
  )

  // Disconnected user → always render the component so it can show the login link
  if (!isAuthenticated) {
    return addToCart
  }

  if (isAvailableForSale) {
    if (isArticlesPriceAndStockLoading) {
      return <ReferencesProductTableAddToCartSkeleton />
    }

    // Connected user, loading done but no displayable price (e.g. isPriceAvailable=false) → hide add-to-cart
    if (!hasPrice) return null

    return addToCart
  }

  if (article.isEquivalentProductsLinkVisible && article.urlKey) {
    return (
      <div className="desktop:text-right">
        <EquivalentProductsLink slug={article.urlKey} className="desktop:justify-end" />
      </div>
    )
  }

  return null
})

export type ReferencesProductRowDesktopProps = {
  article: MergedArticle
  setSelectedQuantity: (quantity: number) => void
  selectedQuantity?: number
  pickupPointOfSale?: ProductAvailabilities_PickupPointOfSaleFragment | null
}

function ReferencesProductRowDesktopComponent({
  article,
  pickupPointOfSale,
  setSelectedQuantity,
  selectedQuantity,
}: ReferencesProductRowDesktopProps) {
  const {
    isPublicPriceEnabled,
    isAuthenticated,
    isMonoDiscriminant,
    isClickAndCollectEnabled,
    cart,
  } = useReferencesProductTableContext()
  const {
    hasPrice,
    displayPrice,
    hasTiersPrices,
    effectiveLoading,
    isAvailabilityLoading,
    isAvailabilityDisplayable,
    isAvailableForSale,
    isProductPriceDisplayable,
  } = useReferencesProductRow({ article })

  const referencesProductArticleDetailShown = useAtomValue(referencesProductArticleDetailShownAtom)

  const isDetailsShown = useMemo(() => {
    return referencesProductArticleDetailShown === article.reference
  }, [article.reference, referencesProductArticleDetailShown])

  const borderClasses = 'border-b-px border-b-brand-2-200'
  const trClassName = useMemo(() => {
    return twMerge('hidden desktop:table-row', !hasTiersPrices && !isDetailsShown && borderClasses)
  }, [hasTiersPrices, isDetailsShown])

  const { t } = useTranslation(['referencesProductTable'])

  const columns = useAtomValue(referencesProductSelectedColumnsAtom)

  return (
    <>
      <tr className={trClassName}>
        <td className="p-2">
          {!effectiveLoading && hasPrice && article.price?.tag && (
            <ProductTag className="mb-1" tag={article.price.tag} />
          )}

          <p>{t('referencesProductTable:company', { ref: article.reference })}</p>
          {article.supplierReference && (
            <p className="mt-0.5">
              {t('referencesProductTable:manufacturer')} {article.supplierReference}
            </p>
          )}
          <ButtonDetailsShown
            reference={article.reference}
            isMonoDiscriminant={isMonoDiscriminant}
          />
        </td>
        <ReferencesProductTechnicalSpecs
          columns={columns}
          additionalTechnicalSpecs={article.additionalTechnicalSpecs}
        />
        <td className="whitespace-nowrap p-2">
          <Suspense
            fallback={<ProductAvailabilitiesSkeleton offset={[{ nbOfLine: 1, title: false }]} />}
          >
            <AvailabilityCell
              pickupPointOfSale={pickupPointOfSale}
              isArticlesPriceAndStockLoading={isAvailabilityLoading}
              isAvailabilityDisplayable={isAvailabilityDisplayable}
              article={article}
              isClickAndCollectEnabled={isClickAndCollectEnabled}
              isProductPriceDisplayable={isProductPriceDisplayable}
            />
          </Suspense>
        </td>
        <td className="whitespace-nowrap p-2 desktop:text-right forbidden-desktop-ref-table:text-left">
          <Suspense fallback={<TextSkeleton className="w-18" size="xl" leading="normal" />}>
            <PriceCell
              isArticlesPriceAndStockLoading={effectiveLoading}
              isAvailableForSale={isAvailableForSale}
              isAuthenticated={isAuthenticated}
              hasPrice={hasPrice}
              isPublicPriceEnabled={isPublicPriceEnabled}
              article={article}
              displayPrice={displayPrice}
              selectedQuantity={selectedQuantity}
              cart={cart}
              isProductPriceDisplayable={isProductPriceDisplayable}
            />
          </Suspense>
        </td>
        <td className="hidden whitespace-nowrap p-2 forbidden-desktop-ref-table:table-cell">
          <ActionCell
            isAvailableForSale={isAvailableForSale}
            isAuthenticated={isAuthenticated}
            isArticlesPriceAndStockLoading={effectiveLoading}
            hasPrice={hasPrice}
            cart={cart}
            article={article}
            setSelectedQuantity={setSelectedQuantity}
            isMonoDiscriminant={isMonoDiscriminant}
          />
        </td>
      </tr>

      {hasTiersPrices && (
        <tr className={twMerge('hidden desktop:table-row', !isDetailsShown && borderClasses)}>
          <td className="px-2 pb-2" colSpan={4 + columns.length}>
            <ProductTiersPricesInfoCard
              packaging={article.stock?.packaging}
              priceQuantity={displayPrice.priceQuantity}
              tiersPrices={displayPrice.tiersPrices}
              selectedQuantity={selectedQuantity}
              isLoading={effectiveLoading}
            />
          </td>
        </tr>
      )}

      {isDetailsShown && (
        <tr>
          <td className="px-2" colSpan={4 + columns.length}>
            <Divider direction="horizontal" variant="dot" />
          </td>
        </tr>
      )}
    </>
  )
}

export const ReferencesProductRowDesktop = React.memo(ReferencesProductRowDesktopComponent)
